import { atom } from "jotai";

import { PatronEditBooking } from "../../../types";
import { currentBookingAtom } from "../../stores";

const isGuest = (round: PatronEditBooking.Round) =>
  ["guest", "newGuest", "unknownGuest"].includes(round.player.clientType);

export const normalizeGroupHosts = (
  rounds: PatronEditBooking.Round[],
  placeUnhostedGuestsAfterFirstMember = false
): PatronEditBooking.Round[] => {
  const groups = new Map<number, PatronEditBooking.Round[]>();
  for (const round of rounds) {
    const index = round.bookingIndex ?? 0;
    groups.set(index, [...(groups.get(index) ?? []), round]);
  }
  const ordered = [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .flatMap(([, group]) => {
      const members = group.filter(
        (round) => round.player.clientType === "member"
      );
      const repaired = group.map((round) => {
        if (!isGuest(round) || !round.hostId) return round;
        const host =
          members.find((member) => member.player.id === round.hostId) ??
          members[0];
        return { ...round, hostId: host?.player.id ?? null };
      });
      const unhostedGuests = placeUnhostedGuestsAfterFirstMember
        ? repaired.filter((round) => isGuest(round) && !round.hostId)
        : [];
      const ordered = repaired
        .filter(
          (round) =>
            (!isGuest(round) || !round.hostId) &&
            (!placeUnhostedGuestsAfterFirstMember || !isGuest(round))
        )
        .flatMap((round) => [
          round,
          ...repaired.filter(
            (guest) =>
              isGuest(guest) && guest.hostId && guest.hostId === round.player.id
          ),
          ...(round === members[0] ? unhostedGuests : []),
        ]);
      return members.length === 0 ? [...unhostedGuests, ...ordered] : ordered;
    });
  const indices = new Map(
    ordered.map((round, index) => [round.localId, index])
  );
  const repairedById = new Map(ordered.map((round) => [round.localId, round]));
  return rounds.map((round) => ({
    ...(repairedById.get(round.localId) ?? round),
    index: indices.get(round.localId) ?? round.index,
  }));
};

export const setHostIdAndIndexOnRoundAtom = atom(
  null,
  (get, set, payload: { roundId: string; hostId: string }) => {
    const booking = get(currentBookingAtom);
    if (!booking) return;

    const selectedRound = booking.rounds.find(
      (round) => round.localId === payload.roundId
    );

    if (
      !selectedRound ||
      !booking.rounds.some(
        (round) =>
          round.player.clientType === "member" &&
          round.player.id === payload.hostId &&
          (round.bookingIndex ?? 0) === (selectedRound.bookingIndex ?? 0)
      )
    )
      return;

    const updatedRounds = booking.rounds.map((round) => {
      if (round.localId === payload.roundId) {
        return {
          ...round,
          hostId: payload.hostId,
        };
      }
      return round;
    });

    set(currentBookingAtom, {
      ...booking,
      rounds: normalizeGroupHosts(updatedRounds).sort(
        (a, b) => a.index - b.index
      ),
    });
  }
);
